import { AvatarProps } from '@mui/material/Avatar';
import { avatarGroupClasses, AvatarGroupProps } from '@mui/material/AvatarGroup';
import { alpha, Theme } from '@mui/material/styles';

import { deepNavy } from '../../palette-v2';

// ----------------------------------------------------------------------

const COLORS = ['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

const colorByName = (name: string) => {
  const charAt = name.charAt(0).toLowerCase();
  if (['a', 'c', 'f'].includes(charAt)) return 'primary';
  if (['e', 'd', 'h'].includes(charAt)) return 'secondary';
  if (['i', 'k', 'l'].includes(charAt)) return 'info';
  if (['m', 'n', 'p'].includes(charAt)) return 'success';
  if (['q', 's', 't'].includes(charAt)) return 'warning';
  if (['v', 'x', 'y'].includes(charAt)) return 'error';
  return 'default';
};

// Estendiamo le varianti se necessario (es. per le taglie custom o varianti compact)
declare module '@mui/material/AvatarGroup' {
  interface AvatarGroupPropsVariantOverrides {
    compact: true;
  }
}

// ----------------------------------------------------------------------

export function avatar(theme: Theme) {
  return {
    MuiAvatar: {
      variants: COLORS.map((color) =>
        color === 'default'
          ? {
              props: { color: 'default' },
              style: {
                color: theme.palette.text.secondary,
                backgroundColor: alpha(theme.palette.grey[500], 0.24),
              },
            }
          : {
              props: { color },
              style: {
                color: theme.palette[color].contrastText,
                backgroundColor: theme.palette[color].main,
              },
            },
      ),

      styleOverrides: {
        root: {
          fontWeight: theme.typography.fontWeightMedium,
        },
        rounded: {
          borderRadius: 12,
        },
        colorDefault: ({ ownerState }: { ownerState: AvatarProps }) => {
          const color = colorByName(`${ownerState.alt}`);

          return {
            ...(!!ownerState.alt && {
              ...(color !== 'default'
                ? {
                    color: theme.palette[color].contrastText,
                    backgroundColor: theme.palette[color].main,
                  }
                : {
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.grey[500], 0.24),
                  }),
            }),
          };
        },
      },
    },
    MuiAvatarGroup: {
      defaultProps: {
        max: 4,
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AvatarGroupProps }) => ({
          justifyContent: 'flex-end',
          // Gestione variante "compact" esistente
          ...(ownerState.variant === 'compact' && {
            width: 40,
            height: 40,
            position: 'relative',
            [`& .${avatarGroupClasses.avatar}`]: {
              margin: 0,
              width: 28,
              height: 28,
              position: 'absolute',
              '&:first-of-type': { left: 0, bottom: 0, zIndex: 9 },
              '&:last-of-type': { top: 0, right: 0 },
            },
          }),
        }),
        avatar: {
          borderWidth: 2,
          // Stile del badge "+X" (l'ultimo cerchio nel gruppo)
          // In MUI l'avatar del conteggio è il primo elemento nel DOM
          '&:first-of-type': {
            fontSize: 12,
            color: theme.palette.text.secondary,
            backgroundColor: deepNavy[300],
            fontWeight: theme.typography.fontWeightBold,
          },
          // Spaziatura tra gli avatar per l'effetto sovrapposizione (Medium/Small del Figma)
          marginLeft: -8,
          [`&.${avatarGroupClasses.avatar}`]: {
            // Assicuriamoci che il bordo sia del colore dello sfondo per "staccare"
            borderColor: theme.palette.background.paper,
          },
        },
      },
    },
  };
}
